import React, { useState, useEffect } from 'react';
import {
  Paper,
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Zoom,
  useTheme as useMuiTheme,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  LibraryMusic as MusicNoteIcon,
  Event as EventIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  Language as LanguageIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { keyframes } from '@mui/system';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import LanguageDialog from '../common/LanguageDialog';
import QuickSettings from '../settings/QuickSettings';
import LogoutDialog from '../common/LogoutDialog';

// Define slide up animation
const slideUp = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const BottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mounted, setMounted] = useState(false);
  const [languageDialogOpen, setLanguageDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const { t } = useTranslation();
  const { user, churchData } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const theme = useMuiTheme();

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleLanguageClick = (event) => {
    event.preventDefault();
    setSettingsDialogOpen(false);
    setLanguageDialogOpen(true);
  };

  const handleSettingsClick = (event) => {
    event.preventDefault();
    setSettingsDialogOpen(true);
  };

  const handleLogoutClick = () => {
    setSettingsDialogOpen(false);
    setLogoutDialogOpen(true);
  };

  const navItems = [
    { label: t('nav.dashboard'), value: "/app", icon: DashboardIcon },
    { label: t('nav.services'), value: "/app/services", icon: EventIcon },
    { label: t('nav.songs'), value: "/app/songs", icon: MusicNoteIcon },
    { label: t('nav.people'), value: "/app/people", icon: PeopleIcon },
    { label: t('nav.settings'), value: "settings", icon: SettingsIcon, onClick: handleSettingsClick },
  ];

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          animation: mounted ? `${slideUp} 0.3s ease-out` : 'none',
        }}
      >
        <Zoom in={mounted} timeout={300}>
          <Paper
            elevation={3}
            sx={{
              borderRadius: '16px 16px 0 0',
              overflow: 'hidden',
              background: theme.palette.background.paper,
              backdropFilter: 'blur(10px)',
            }}
          >
            <BottomNavigation
              value={location.pathname}
              onChange={(event, newValue) => {
                if (!['settings', 'language'].includes(newValue)) {
                  navigate(newValue);
                }
              }}
              showLabels
              sx={{
                height: 65,
                background: 'transparent',
                '& .MuiBottomNavigationAction-root': {
                  color: 'text.secondary',
                  minWidth: 'auto',
                  padding: '6px 8px',
                  '&.Mui-selected': {
                    color: 'primary.main',
                  },
                },
              }}
            >
              {navItems.map((item) => (
                <BottomNavigationAction
                  key={item.value}
                  label={item.label}
                  value={item.value}
                  icon={<item.icon />}
                  onClick={item.onClick}
                  sx={{
                    minWidth: 'auto',
                    padding: '6px 8px',
                  }}
                />
              ))}
            </BottomNavigation>
          </Paper>
        </Zoom>
      </Box>

      <QuickSettings
        open={settingsDialogOpen}
        onClose={() => setSettingsDialogOpen(false)}
        onLanguageClick={handleLanguageClick}
        onLogoutClick={handleLogoutClick}
        onThemeToggle={toggleTheme}
        isDarkMode={isDarkMode}
        churchName={churchData?.name}
        churchCode={churchData?.confirmationCode}
      />

      <LanguageDialog
        open={languageDialogOpen}
        onClose={() => setLanguageDialogOpen(false)}
      />

      <LogoutDialog
        open={logoutDialogOpen}
        onClose={() => setLogoutDialogOpen(false)}
      />
    </>
  );
};

export default BottomNav;
